import moment from 'moment';
import { LocationPros } from '../../../../types/baseTypes';

/**
 * Opening hours are shown for every location that has them — a room's as
 * much as a station's, and "0:00 – 24:00" as much as "8:00 – 22:00": when a
 * place is open is worth knowing either way (PO, 2026-10-19).
 *
 * Until then they were hidden whenever the location's (or tenant's) use cases
 * were only venue/apartment, a rule from before rooms had opening hours of
 * their own. A tenant can still switch the block off
 * (`showOpeningHoursOnLocationsPage`, `showOpeningHoursInBookingFlow`).
 */
const isValidEntry = (entry: LocationPros['openingHours'][number] | undefined): boolean => {
  const fromHour = entry?.times?.fromHour;
  const toHour = entry?.times?.toHour;
  return (
    Number.isFinite(fromHour) &&
    Number.isFinite(toHour) &&
    (fromHour as number) >= 0 &&
    (toHour as number) <= 24 &&
    (fromHour as number) < (toHour as number)
  );
};

/** Whether the location has at least one valid opening-hours entry to show. */
export function shouldShowOpeningHours(selectedLocation: LocationPros | null): boolean {
  return (selectedLocation?.openingHours ?? []).some(isValidEntry);
}

/**
 * An opening hour as text. 24 is the END of the day: in a 24-hour format it
 * reads "24:00" — moment would roll it over to "0:00", so "0:00 - 0:00" for a
 * place open around the clock. A 12-hour format keeps moment's midnight
 * ("12:00 AM"), which is how that notation writes it.
 */
export const formatOpeningHour = (hour: number, timeFormat: string): string =>
  hour === 24 && !timeFormat.includes('A')
    ? '24:00'
    : moment({ hour, minute: 0 }).format(timeFormat);

export default shouldShowOpeningHours;
